import { useRef, useState } from 'react'
import { Check, FileUp, Loader2, TriangleAlert, Upload, X } from 'lucide-react'
import { errorMessage } from '@/lib/api'
import { ARTWORK_ACCEPT, ARTWORK_HINT, ArtworkUploadError, FONT_ACCEPT, FONT_HINT, MAX_ARTWORKS, MAX_FONTS, artworkExt, asItemArtwork, fileSize, uploadArtwork, uploadFont, type Artwork } from '@/lib/artwork'
import { useToast } from '@/components/ui/toast'
import { ArtworkThumb, ArtworkViewer } from '@/components/order/ArtworkGallery'

export type { Artwork }

/**
 * What the picker takes. Fonts (client request 2026-10-14) reuse the whole
 * picker with their own upload, limits and words.
 */
const KINDS = {
  artwork: {
    accept: ARTWORK_ACCEPT,
    max: MAX_ARTWORKS,
    upload: uploadArtwork,
    hint: `${ARTWORK_HINT}. Up to ${MAX_ARTWORKS} files, e.g. front and back.`,
    attach: 'Attach artwork',
    another: 'Add another file (e.g. the back)',
    anotherDrop: 'another file (e.g. the back)',
  },
  font: {
    accept: FONT_ACCEPT,
    max: MAX_FONTS,
    upload: uploadFont,
    hint: `${FONT_HINT}. Up to ${MAX_FONTS} files.`,
    attach: 'Attach font file',
    another: 'Add another font',
    anotherDrop: 'another font',
  },
} as const

/**
 * Uploads print files and hands back what the order needs to keep. Several
 * per item, in order, because a design often comes as a front and a back
 * picture (client request 2026-10-05); the job sheet prints them in this
 * order.
 *
 * Used at checkout rather than on the product page, because the upload
 * endpoint requires a signed-in customer and the marketplace deliberately lets
 * guests configure everything before they sign in.
 */
export default function ArtworkPicker({
  value,
  fallbackName,
  basePath = '',
  variant = 'link',
  preview = true,
  kind = 'artwork',
  onChange,
}: {
  value: Artwork[]
  fallbackName?: string | null
  /** '' for customer checkout, '/admin' or '/sales' for the manual order form. */
  basePath?: string
  /**
   * 'link' is the compact line checkout uses beside each cart row. 'dropzone'
   * is the drag-and-drop box from the product page, for the team's order
   * form and Edit artwork, where the link was too small to find.
   */
  variant?: 'link' | 'dropzone'
  /**
   * Whether a dropzone thumbnail opens the preview dialog. Off on the
   * storefront, where its console buttons do not belong.
   */
  preview?: boolean
  /** Artwork by default; 'font' for the font behind names and numbers. */
  kind?: keyof typeof KINDS
  onChange: (artworks: Artwork[]) => void
}) {
  const config = KINDS[kind]
  const toast = useToast()
  const input = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  // 0-100 for the file going up now; null when the upload cannot say (fonts).
  const [percent, setPercent] = useState<number | null>(null)
  const [dragging, setDragging] = useState(false)
  const [previewIndex, setPreviewIndex] = useState<number | null>(null)
  const files = value.map(asItemArtwork)

  // One at a time, so the files land in the order they were picked.
  const upload = async (files: File[]) => {
    const room = config.max - value.length
    if (files.length > room) toast.error('Too many files', `Up to ${config.max} files per item.`)

    setUploading(true)
    let next = value
    try {
      for (const file of files.slice(0, Math.max(0, room))) {
        setPercent(null)
        next = [...next, await config.upload(file, basePath, setPercent)]
        onChange(next)
      }
    } catch (err) {
      toast.error('Upload failed', err instanceof ArtworkUploadError ? err.message : errorMessage(err))
    } finally {
      setUploading(false)
      setPercent(null)
      // Clear the input so picking the same file again still fires onChange.
      if (input.current) input.current.value = ''
    }
  }

  // A named-but-not-uploaded file is a trap that has already produced job
  // sheets with nothing behind the filename, so it warns instead of offering
  // a quiet link that reads as "already attached".
  const warn = value.length === 0 && fallbackName && !uploading
  const remove = (index: number) => onChange(value.filter((_, i) => i !== index))

  if (variant === 'dropzone') {
    return (
      <div className="mt-2 space-y-2">
        {value.map((file, index) => (
          <div
            key={`${file.artwork_key}-${index}`}
            className="flex items-center gap-3 rounded-xl border border-foreground/10 bg-background p-2.5"
          >
            {preview ? (
              <button
                type="button"
                title="Preview"
                aria-label={`Preview ${file.artwork_name}`}
                onClick={() => setPreviewIndex(index)}
                className="group shrink-0 rounded-lg focus:outline-none"
              >
                <ArtworkThumb
                  file={files[index]}
                  path={file.artwork_path}
                  className="size-14 transition-colors group-hover:border-primary/60 group-focus-visible:ring-2 group-focus-visible:ring-primary/30"
                />
              </button>
            ) : (
              <ArtworkThumb file={files[index]} path={file.artwork_path} className="size-14" />
            )}
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-bold text-foreground">
                {value.length > 1 && `${index + 1}. `}
                {file.artwork_name}
              </p>
              <p className="mt-0.5 flex items-center gap-1 text-2xs text-foreground/50">
                <Check className="size-3 shrink-0 text-success" />
                {['Uploaded', artworkExt(files[index]), fileSize(file.artwork_size)].filter(Boolean).join(' · ')}
              </p>
            </div>
            <button
              type="button"
              className="-m-2 p-2 text-foreground/40 transition hover:text-danger"
              onClick={() => remove(index)}
              aria-label={`Remove ${file.artwork_name}`}
            >
              <X className="size-4" />
            </button>
          </div>
        ))}

        {warn && (
          <p className="flex items-center gap-1.5 text-xs font-semibold text-warning">
            <TriangleAlert className="size-3.5 shrink-0" />
            {fallbackName} is not uploaded yet - attach the file
          </p>
        )}

        {value.length < config.max && (
          <label
            className={
              dragging
                ? 'block cursor-pointer rounded-xl border-2 border-dashed border-primary bg-primary/5 p-5 text-center transition-colors'
                : 'block cursor-pointer rounded-xl border-2 border-dashed border-foreground/20 bg-background p-5 text-center transition-colors hover:border-primary/50'
            }
            onDragOver={(e) => {
              e.preventDefault()
              setDragging(true)
            }}
            onDragLeave={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragging(false)
            }}
            onDrop={(e) => {
              e.preventDefault()
              setDragging(false)
              const files = Array.from(e.dataTransfer.files ?? [])
              if (files.length > 0 && !uploading) void upload(files)
            }}
          >
            <span className="flex items-center justify-center gap-2 text-sm text-foreground/50">
              {uploading ? (
                <>
                  <Loader2 className="size-4 animate-spin text-primary" />
                  <span>{percent === null ? 'Uploading...' : `Uploading... ${percent}%`}</span>
                </>
              ) : (
                <>
                  <Upload className="size-4 shrink-0" />
                  <span>
                    Drag &amp; drop or{' '}
                    {value.length > 0 ? (
                      <>
                        <span className="font-bold text-primary">browse</span> {config.anotherDrop}
                      </>
                    ) : (
                      <span className="font-bold text-primary">browse files</span>
                    )}
                  </span>
                </>
              )}
            </span>
            <span className="mt-1 block text-2xs text-foreground/40">{config.hint}</span>
            <input
              ref={input}
              type="file"
              multiple
              className="hidden"
              accept={config.accept}
              disabled={uploading}
              onChange={(e) => {
                const files = Array.from(e.target.files ?? [])
                if (files.length > 0) void upload(files)
              }}
            />
          </label>
        )}

        {preview && <ArtworkViewer files={files} index={previewIndex} onIndex={setPreviewIndex} />}
      </div>
    )
  }

  return (
    <div className="mt-1">
      {value.map((file, index) => (
        // Checkout shows the picture without the preview dialog, whose
        // console buttons do not belong on the storefront.
        <div key={`${file.artwork_key}-${index}`} className="mb-1 flex items-center gap-2 text-xs text-status-success">
          <ArtworkThumb file={files[index]} path={file.artwork_path} className="size-10" />
          {value.length > 1 && <span className="shrink-0 font-semibold">{index + 1}.</span>}
          <span className="min-w-0 truncate">{file.artwork_name}</span>
          <Check className="size-3.5 shrink-0" />
          <button
            type="button"
            className="-m-2 p-2 text-foreground/40 hover:text-status-danger"
            onClick={() => remove(index)}
            aria-label={`Remove ${file.artwork_name}`}
          >
            <X className="size-3.5" />
          </button>
        </div>
      ))}

      {value.length < config.max && (
        <label
          className={
            warn
              ? 'mt-0.5 flex cursor-pointer items-center gap-1.5 text-xs font-semibold text-warning hover:underline'
              : 'mt-0.5 flex cursor-pointer items-center gap-1.5 text-xs font-semibold text-primary hover:underline'
          }
        >
          {uploading ? (
            <Loader2 className="size-3.5 shrink-0 animate-spin" />
          ) : warn ? (
            <TriangleAlert className="size-3.5 shrink-0" />
          ) : (
            <FileUp className="size-3.5 shrink-0" />
          )}
          {uploading
            ? percent === null
              ? 'Uploading...'
              : `Uploading... ${percent}%`
            : warn
              ? `${fallbackName} is not uploaded yet - attach the file`
              : value.length > 0
                ? config.another
                : config.attach}
          <input
            ref={input}
            type="file"
            multiple
            className="hidden"
            accept={config.accept}
            disabled={uploading}
            onChange={(e) => {
              const files = Array.from(e.target.files ?? [])
              if (files.length > 0) void upload(files)
            }}
          />
        </label>
      )}
      {/* Asked by the client, who could not tell what the link takes. */}
      <p className="mt-0.5 text-2xs text-foreground/50">{config.hint}</p>
    </div>
  )
}
